/* eslint-disable max-lines */
import React, { useCallback, useContext, useState } from 'react';
import { CalendarInput, TextInput } from '../../UI';
import { PropTypes } from 'prop-types';
import MessagesContext from '../../../contexts/messagesContext';
import { makeStyles } from '@material-ui/core';
import { DateObject } from 'react-multi-date-picker';
import { useDispatch } from 'react-redux';
import { addErrorMessage, addValue } from '../../../redux/Search/SearchAction';
import { useNonInitialEffect } from '../../../hooks/useNonInitialEffect';
import SelectionForm from './SelectionForm';
import LanguageContext from '../../../contexts/languageContext';
import { addSelectValue } from '../../../redux/Search/SearchAction';

const styles = makeStyles({
    fieldValueWrapper: {
        width: '360px',
        marginInlineEnd: '8px'
    },
    row: {
        display: 'flex',
        alignItems: 'flex-end'
    },
    inputContainer: {
        width: '100%',
        marginInlineEnd: '8px'
    },
    input: {
        border: '1px solid #e8ecf2',
        borderRadius: '4px',
        background: '#fff',
        height: '39px',
        display: 'flex',
        alignItems: 'center',
        '& > div': {
            width: '100%'
        },
        '& input': {
            height: '100%',
            padding: '8px'
        }
    },
    text: {
        marginBottom: '10px',
        marginInlineEnd: '8px',
        fontSize: '13px',
        color: '#545e66'
    },
    inputRoot: {
        paddingRight: '10px !important',
        border: 'solid 1px #e8ecf2',
        backgroundColor: '#ffffff',
        fontSize: '16px',
        '&:focus-within': {
            border: 'solid 1px #1b90fa'
        }
    },
    tag: {
        borderRadius: '4px',
        backgroundColor: '#e8ecf2',
        color: '#232733',
        fontFamily: 'inherit',
        fontSize: '13px',
        '& svg': {
            margin: '0px 3px !important'
            // backgroundColor: 'transparent'
        }
    },
    title: {
        fontFamily: 'mainFont',
        fontSize: '13px',
        fontWeight: '500',
        color: '#545e66',
        paddingBottom: '8px !important'
    },
    datePickerContainer: {
        width: '100%',
        '& label': {
            fontSize: '13px',
            color: '#545e66'
        },
        '& > div': {
            minWidth: ({ hasTime, rtl }) => (!rtl && hasTime ? '220px' : !rtl ? '170px' : 'unset')
        },
        '&:nth-of-type(1)': {
            marginInlineEnd: '8px'
        },
        '& > div > div': {
            background: 'white',
            border: '1px solid #e8ecf2'
        }
    },
    fieldInputWrapper: {
        position: 'relative'
    },
    errorMessage: {
        color: 'red',
        fontSize: '12px',
        fontFamily: 'inherit',
        position: 'absolute',
        bottom: '-20px',
        width: '100%',
        right: '0'
    }
});
const FieldValue = ({ item, firstIndex, field }) => {
    const { dataType, metaData } = field;
    const { index } = item;

    const { lang } = useContext(LanguageContext);
    const classes = styles({ hasTime: dataType === 'datetime', rtl: lang === 'fa' });
    const { messages } = useContext(MessagesContext);
    const [selectValue, setSelectValue] = useState({ id: null, name: null });
    const [dateValue, setDateValue] = useState({ from: '', to: '' });
    const [numberValue, setNumberValue] = useState({ from: null, value: null });
    const dispatch = useDispatch();

    const selectOptions = metaData?.items
        ?.filter((item) => item[0] && item[1])
        ?.map((item) => ({ id: item[0], name: item[1], fieldName: field.value.id }));

    const handleChangeText = useCallback((e) => {
        const value = e.target.value;
        if (value?.length >= 30) {
            dispatch(addErrorMessage({ index, message: messages.validationSearch.maxOfWord }));
        } else {
            dispatch(
                addValue({
                    index,
                    value: value ? value : null
                })
            );
            dispatch(addErrorMessage({ index, message: null }));
        }
    }, []);

    const handleChangeDate = (date, type) => {
        if (dataType === 'date') {
            if (type === 'from') {
                setDateValue({ from: date, to: dateValue.to });
            } else if (type === 'to') {
                setDateValue({ from: dateValue.from, to: date });
            }
        } else {
            if (type === 'from') {
                setDateValue({ from: date?.toDate(), to: dateValue.to });
            } else if (type === 'to') {
                setDateValue({ from: dateValue.from, to: date?.toDate() });
            }
        }
    };
    useNonInitialEffect(() => {
        const { from, to } = dateValue;
        const fromDate = new Date(from);
        const toDate = new Date(to);
        if (from && to) {
            if (toDate <= fromDate) {
                dispatch(
                    addErrorMessage({ index, message: messages.validationSearch.maxAndMinValue })
                );
            } else {
                dispatch(
                    addValue({
                        index,
                        value: [dateValue.from, dateValue.to],
                        isRange: true
                    })
                );
                dispatch(addErrorMessage({ index, message: null }));
            }
        } else {
            dispatch(
                addValue({
                    index,
                    value: null
                })
            );
            dispatch(addErrorMessage({ index, message: null }));
        }
    }, [dateValue]);

    useNonInitialEffect(() => {
        if (numberValue?.from?.toString() && numberValue?.to?.toString()) {
            if (numberValue.to <= numberValue.from) {
                dispatch(
                    addErrorMessage({ index, message: messages.validationSearch.maxAndMinValue })
                );
            } else {
                dispatch(
                    addValue({
                        index,
                        value: [numberValue.from, numberValue.to],
                        isRange: true
                    })
                );
                dispatch(addErrorMessage({ index, message: null }));
            }
        } else {
            dispatch(
                addValue({
                    index,
                    value: null
                })
            );
            dispatch(addErrorMessage({ index, message: null }));
        }
    }, [numberValue]);

    useNonInitialEffect(() => {
        dispatch(addErrorMessage({ index, message: null }));
        setSelectValue({ id: null, name: null });
        setDateValue({});
        setNumberValue({});
    }, [item?.structure, item?.field]);
    // when changed field or form drop down out state should be reset

    return (
        <>
            <div className={classes.fieldValueWrapper}>
                {dataType === 'char' || dataType === 'text' ? (
                    <div className={classes.fieldInputWrapper}>
                        <TextInput
                            label={firstIndex && messages.fieldValue}
                            className={classes.inputContainer}
                            value={item?.value || ''}
                            customClasses={classes}
                            onChange={handleChangeText}
                        />
                        {item?.errorMessage && (
                            <span className={classes.errorMessage}>{item?.errorMessage}</span>
                        )}
                    </div>
                ) : dataType === 'select' ? (
                    <div className={classes.fieldInputWrapper}>
                        <SelectionForm
                            placeholder="select value"
                            value={selectValue}
                            options={selectOptions}
                            label={firstIndex && messages.fieldValue}
                            handleChange={(val) => {
                                const id = val?.id;
                                const findValue = selectOptions.find((item) => item.id === id);
                                setSelectValue(findValue);
                                dispatch(
                                    addValue({
                                        index,
                                        value: id?.length ? id : null
                                    })
                                );
                                dispatch(
                                    addSelectValue({
                                        id: id?.length ? id : null,
                                        name: findValue?.fieldName,
                                        title: findValue?.name
                                    })
                                );
                            }}
                        />
                    </div>
                ) : dataType === 'integer' || dataType === 'float' ? (
                    <div className={classes.fieldInputWrapper}>
                        <div className={classes.row}>
                            <span className={classes.text}>{messages.from}</span>
                            <TextInput
                                label={firstIndex && messages.fieldValue}
                                isNumber
                                className={classes.inputContainer}
                                customClasses={classes}
                                onChange={({ target: { value } }) => {
                                    setNumberValue({ from: +value, to: numberValue.to });
                                }}
                            />
                            <span className={classes.text}>{messages.to}</span>
                            <TextInput
                                isNumber
                                className={classes.inputContainer}
                                customClasses={classes}
                                onChange={({ target: { value } }) =>
                                    setNumberValue({ from: numberValue.from, to: +value })
                                }
                            />
                        </div>
                        {item?.errorMessage && (
                            <span className={classes.errorMessage}>{item?.errorMessage}</span>
                        )}
                    </div>
                ) : dataType === 'date' ? (
                    <div className={classes.fieldInputWrapper}>
                        <div className={classes.row}>
                            <span className={classes.text}>{messages.from}</span>
                            <div className={classes.datePickerContainer}>
                                <CalendarInput
                                    width="100%"
                                    label={firstIndex && messages.fieldValue}
                                    value={dateValue.from}
                                    setValue={(date) => handleChangeDate(date, 'from')}
                                />
                            </div>
                            <span className={classes.text}>{messages.to}</span>
                            <div className={classes.datePickerContainer}>
                                <CalendarInput
                                    width="100%"
                                    value={dateValue.to}
                                    setValue={(date) => handleChangeDate(date, 'to')}
                                />
                            </div>
                        </div>
                        {item?.errorMessage && (
                            <span className={classes.errorMessage}>{item?.errorMessage}</span>
                        )}
                    </div>
                ) : dataType === 'datetime' ? (
                    <div className={classes.fieldInputWrapper}>
                        <div className={classes.row}>
                            <span className={classes.text}>{messages.from}</span>
                            <div className={classes.datePickerContainer}>
                                <CalendarInput
                                    width="100%"
                                    label={firstIndex && messages.fieldValue}
                                    format="YYYY/MM/DD hh:mm"
                                    value={dateValue.from}
                                    hasTimePicker
                                    onChange={(date) =>
                                        handleChangeDate(new DateObject(date), 'from')
                                    }
                                />
                            </div>
                            <span className={classes.text}>{messages.to}</span>
                            <div className={classes.datePickerContainer}>
                                <CalendarInput
                                    width="100%"
                                    label={firstIndex && messages.fieldValue}
                                    format="YYYY/MM/DD hh:mm"
                                    value={dateValue.to}
                                    hasTimePicker
                                    onChange={(date) =>
                                        handleChangeDate(new DateObject(date), 'to')
                                    }
                                />
                            </div>
                        </div>
                        {item?.errorMessage && (
                            <span className={classes.errorMessage}>{item?.errorMessage}</span>
                        )}
                    </div>
                ) : null}
            </div>
        </>
    );
};

FieldValue.propTypes = {
    item: PropTypes.object,
    firstIndex: PropTypes.number,
    field: PropTypes.object
};

export default React.memo(FieldValue);
